/**
 * Core Web Vitals Tracking
 *
 * Tracks LCP, FID, and CLS metrics in Mixpanel for performance correlation
 * with conversion funnel analysis.
 *
 * @see https://web.dev/vitals/
 * @see https://github.com/GoogleChrome/web-vitals
 */

import {type Metric, onCLS, onINP, onLCP} from 'web-vitals';
import {trackEvent} from '@/shared/utils/mixpanel';

/**
 * Track Core Web Vitals metrics in Mixpanel
 *
 * Metrics tracked:
 * - LCP (Largest Contentful Paint): Loading performance
 * - INP (Interaction to Next Paint): Interactivity (replaces FID)
 * - CLS (Cumulative Layout Shift): Visual stability
 *
 * These metrics are automatically tracked and sent to Mixpanel with ratings
 * (good/needs-improvement/poor) for easy filtering and analysis.
 */
export function trackCoreWebVitals(): void {
  // Largest Contentful Paint (LCP)
  // Target: < 2.5s (good), < 4.0s (needs-improvement), >= 4.0s (poor)
  onLCP((metric: Metric) => {
    trackEvent('Performance: Core Web Vital', {
      metric_name: 'LCP',
      value: Math.round(metric.value), // Round to nearest ms
      rating: metric.rating, // 'good' | 'needs-improvement' | 'poor'
      delta: metric.delta ? Math.round(metric.delta) : undefined,
      id: metric.id,
      navigation_type: metric.navigationType, // 'navigate' | 'reload' | 'back-forward' | 'prerender'
    });
  });

  // Interaction to Next Paint (INP) - Replaces FID in Core Web Vitals
  // Target: < 200ms (good), < 500ms (needs-improvement), >= 500ms (poor)
  onINP((metric: Metric) => {
    trackEvent('Performance: Core Web Vital', {
      metric_name: 'INP',
      value: Math.round(metric.value), // Round to nearest ms
      rating: metric.rating,
      delta: metric.delta ? Math.round(metric.delta) : undefined,
      id: metric.id,
    });
  });

  // Cumulative Layout Shift (CLS)
  // Target: < 0.1 (good), < 0.25 (needs-improvement), >= 0.25 (poor)
  onCLS((metric: Metric) => {
    trackEvent('Performance: Core Web Vital', {
      metric_name: 'CLS',
      value: Math.round(metric.value * 1000) / 1000, // Round to 3 decimal places
      rating: metric.rating,
      delta: metric.delta ? Math.round(metric.delta * 1000) / 1000 : undefined,
      id: metric.id,
    });
  });
}
